import api from './axios'

// Client for plans (what each tier includes) and per-company feature
// entitlements — CITS-Server/src/routes/admin/features.js.
//
// Model: a company's entitlement is its tier's plan plus per-company
// exceptions (plan_override 'added' | 'removed'). The company admin's own
// on/off switch is is_enabled; is_active is both (and a child feature is off
// when its parent is).

export type Tier = 'basic' | 'standard' | 'premium' | 'enterprise'
export type FeaturePlatform = 'web' | 'mobile' | 'both'
export type PlanOverride = 'added' | 'removed' | null

export const TIERS: Tier[] = ['basic', 'standard', 'premium', 'enterprise']
export const TIER_LABEL: Record<string, string> = {
  basic: 'Basic',
  standard: 'Standard',
  premium: 'Premium',
  enterprise: 'Enterprise',
}
export const tierName = (t: string | null | undefined) => (t ? TIER_LABEL[t] ?? t.charAt(0).toUpperCase() + t.slice(1) : 'Standard')

export interface FeatureDefinition {
  feature_key: string
  feature_name: string
  description: string | null
  category: string | null
  platform?: FeaturePlatform | null
  parent_key?: string | null
  is_core?: number | boolean | null
  app_group?: string | null
  sort_order?: number | string | null
  requires_tier?: string | null
}

export interface PlansResponse {
  tiers: Tier[]
  definitions: FeatureDefinition[]
  plan: Record<string, string[]>
  company_counts: Record<string, number>
}

export interface PlanChange {
  tier: Tier
  feature_key: string
  included: boolean
}

export interface CompanyFeature extends FeatureDefinition {
  tier_requirement?: string | null
  is_unlocked: number | boolean
  is_enabled: number | boolean
  is_active: number | boolean
  plan_override?: PlanOverride
  in_plan?: number | boolean
  unlocked_at?: string | null
  enabled_at?: string | null
}

export interface CompanyFeaturesResponse {
  features: CompanyFeature[]
  subscription_tier: string | null
  plans_ready: boolean
}

export interface TierPreview {
  tier: string
  gains: { feature_key: string; feature_name: string }[]
  losses: { feature_key: string; feature_name: string }[]
}

/** The 501 the plan endpoints answer until the registry migration has run. */
export function isPlanSchemaMissing(err: unknown): boolean {
  const e = err as { response?: { status?: number; data?: { code?: string } } }
  return e?.response?.status === 501 && e.response.data?.code === 'PLAN_SCHEMA_MISSING'
}

export const PLAN_MIGRATION = 'migrations-2026-9-28-app-features'
/** Registry migrations live on this Database Tools tab. */
export const PLAN_MIGRATION_ROUTE = '/database?tab=registry'

export async function getPlans(): Promise<PlansResponse> {
  const r = await api.get('/super-admin/plans')
  return r.data
}

export async function savePlans(changes: PlanChange[]): Promise<{ applied: number; companies: number }> {
  const r = await api.put('/super-admin/plans', { changes })
  return { applied: Number(r.data?.applied ?? 0), companies: Number(r.data?.companies ?? 0) }
}

export async function getCompanyFeatures(companyId: string): Promise<CompanyFeaturesResponse> {
  const r = await api.get(`/super-admin/features/company/${companyId}`)
  return {
    features: r.data?.features ?? [],
    subscription_tier: r.data?.subscription_tier ?? null,
    plans_ready: !!r.data?.plans_ready,
  }
}

export async function setFeatureOverride(companyId: string, featureKey: string, override: PlanOverride): Promise<void> {
  await api.post('/super-admin/features/override', { company_id: companyId, feature_key: featureKey, override })
}

/** Legacy entitlement switch, used only until the plans migration has run. */
export async function toggleFeatureUnlocked(companyId: string, featureKey: string, isUnlocked: boolean): Promise<void> {
  await api.post('/super-admin/features/toggle', { company_id: companyId, feature_key: featureKey, is_unlocked: isUnlocked })
}

export async function previewTierChange(companyId: string, tier: string): Promise<TierPreview> {
  const r = await api.get(`/super-admin/features/company/${companyId}/tier-preview`, { params: { tier } })
  return r.data
}

/** Same call Companies.tsx makes for a tier change; the server recalculates entitlements. */
export async function changeCompanyTier(companyId: string, tier: string): Promise<void> {
  await api.put(`/super-admin/companies/${companyId}`, { subscription_tier: tier })
}

// ── Grouping shared by the Plans matrix and the company view ──────────────

const bool = (v: unknown) => v === true || v === 1 || v === '1'
export const isCore = (f: FeatureDefinition) => bool(f.is_core)
export const truthy = bool

export interface FeatureGroup<T extends FeatureDefinition> {
  key: string
  label: string
  items: T[]
}

const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

/**
 * Crew-app features (platform mobile/both) first, by app_group in catalog
 * order ("Crew app: Every shift", ...), then portal-only features by category.
 */
export function groupFeatures<T extends FeatureDefinition>(list: T[]): FeatureGroup<T>[] {
  const app = list.filter((f) => f.platform === 'mobile' || f.platform === 'both')
  const web = list.filter((f) => !(f.platform === 'mobile' || f.platform === 'both'))
  const order = (f: T) => Number(f.sort_order ?? 0)
  const byGroup = new Map<string, T[]>()
  // sort_order is only meaningful within a group; groups follow the catalog's
  // order on the App features page (APP_GROUP_ORDER), unknown ones last.
  const appSorted = [...app].sort((a, b) => order(a) - order(b) || a.feature_name.localeCompare(b.feature_name))
  for (const f of appSorted) {
    const g = f.app_group || 'Other'
    byGroup.set(g, [...(byGroup.get(g) || []), f])
  }
  const APP_GROUP_ORDER = ['Every shift', 'Work', 'Safety & equipment', 'Location', 'Seasonal & meetings']
  const appGroups = [...byGroup.entries()]
    .sort(([a], [b]) => {
      const ia = APP_GROUP_ORDER.indexOf(a)
      const ib = APP_GROUP_ORDER.indexOf(b)
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b)
    })
    .map(([g, items]) => ({ key: `app:${g}`, label: `Crew app: ${g}`, items }))

  const byCat = new Map<string, T[]>()
  for (const f of web) {
    const c = f.category || 'other'
    byCat.set(c, [...(byCat.get(c) || []), f])
  }
  const webGroups = [...byCat.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([c, items]) => ({
      key: `web:${c}`,
      label: `Portal: ${titleCase(c)}`,
      items: [...items].sort((a, b) => order(a) - order(b) || a.feature_name.localeCompare(b.feature_name)),
    }))
  return [...appGroups, ...webGroups]
}
